import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import { kbfAPI, errMsg, fmtDate, safeHref } from '../services/intelligenceApi';
import { TierBadge, VerdictBadge, RoleChips } from './IntelligenceKb';
import './Intelligence.css';

/* INTELLIGENCE → KB REVIEW → one draft (brain v2 PLAN-CONCIERGE-FACTORY §5.6, §6.4, §9.2).
 *
 * Drafter (Claude) and verifier (OpenAI, blind) RuleSpecs side by side, disagreements in red, both quotes;
 * the source viewer (stored text with the quote marked, or the stored page as its own sandboxed document,
 * never inlined); the factory's checks; and only the actions apbe allows this reviewer.
 * Everything here is rendered as text: no stored HTML ever reaches this page's DOM. */

/** Same path format as apbe services/kbfTier.js flatten (trigger.all[0].value). */
function flatten(obj, prefix = '', out = {}) {
  if (obj && typeof obj === 'object' && !Array.isArray(obj)) {
    const keys = Object.keys(obj);
    if (!keys.length && prefix) out[prefix] = {};
    for (const k of keys) flatten(obj[k], prefix ? `${prefix}.${k}` : k, out);
  } else if (Array.isArray(obj)) {
    if (!obj.length && prefix) out[prefix] = [];
    obj.forEach((v, i) => flatten(v, `${prefix}[${i}]`, out));
  } else if (prefix) {
    out[prefix] = obj;
  }
  return out;
}
const show = (v) => (v === undefined ? '—' : v === null ? 'null' : typeof v === 'string' ? v : JSON.stringify(v));
const isRefPath = (p) => /(^|\.)quote_ref$/.test(p);
const parentOf = (p) => p.replace(/(\.[^.[\]]+|\[\d+\])$/, '');
const touches = (path, field) => path === field || path.startsWith(`${field}.`) || path.startsWith(`${field}[`) || field.startsWith(`${path}.`);

function Json({ value, open = false, label = 'JSON' }) {
  if (value == null) return <span className="in-muted">—</span>;
  return (
    <details open={open}>
      <summary className="in-muted">{label}</summary>
      <pre className="in-pre">{JSON.stringify(value, null, 2)}</pre>
    </details>
  );
}

function EvidenceQuote({ ev, side, onView, active }) {
  if (!ev) return null;
  const foreign = ev.lang && !['en', 'und'].includes(ev.lang);
  return (
    <div className={`in-quote ${side === 'verifier' ? 'ver' : ''} ${active ? 'active' : ''}`}>
      <span className="in-muted">
        <b>{ev.id}</b> · {ev.source_id} · {ev.via || '—'}{ev.page != null ? ` · page ${ev.page}` : ''}{foreign ? ` · ${ev.lang}` : ''}
        {ev.table_extraction && <span className="in-badge warn" style={{ marginLeft: 6 }}>table extraction</span>}
      </span>
      {foreign && <span className="in-muted">Original ({ev.lang}):</span>}
      <div>{ev.quote}</div>
      {foreign && (
        <div className="in-translation">
          <span className="in-muted">Machine translation (not reviewed; the original is what counts):</span>
          <div>{ev.quote_en || <i>no translation stored</i>}</div>
        </div>
      )}
      {onView && <button type="button" className="in-btn small" style={{ marginTop: 6 }} onClick={() => onView(ev)}>View in source</button>}
    </div>
  );
}

/* ── drafter vs verifier ─────────────────────────────────────────────── */

function RuleSpecCompare({ draft, onView }) {
  const v = draft.verifier || {};
  const fa = useMemo(() => flatten(draft.rulespec || {}), [draft.rulespec]);
  const fb = useMemo(() => flatten(v.rulespec || {}), [v.rulespec]);
  const diff = v.diff || [];
  const dEv = useMemo(() => new Map((draft.evidence || []).map((e) => [e.id, e])), [draft.evidence]);
  const vEv = useMemo(() => new Map((v.evidence || []).map((e) => [e.id, e])), [v.evidence]);
  const findEv = (id) => dEv.get(id) || vEv.get(id);

  const paths = useMemo(() => {
    const seen = new Set();
    const out = [];
    for (const p of [...Object.keys(fa), ...Object.keys(fb)]) {
      if (!seen.has(p) && !isRefPath(p)) {
        seen.add(p);
        out.push(p);
      }
    }
    return out;
  }, [fa, fb]);

  const rows = paths.map((p) => {
    const differs = show(fa[p]) !== show(fb[p]);
    const factory = diff.filter((d) => d && d.field && touches(p, d.field));
    const parent = parentOf(p);
    return { p, a: fa[p], b: fb[p], red: differs || factory.length > 0, refA: fa[`${parent}.quote_ref`], refB: fb[`${parent}.quote_ref`] };
  });
  const unmatched = diff.filter((d) => d && d.field && !paths.some((p) => touches(p, d.field)));
  const redCount = rows.filter((r) => r.red).length;

  if (draft.verdict === 'manual') {
    return (
      <div className="in-card">
        <h3>A person's change (KB RULES)</h3>
        <div className="in-banner info">
          {draft.action === 'signoff' ? 'Approve as is' : 'An edit'} started from <Link to={`/intelligence/kb/rules/${encodeURIComponent(draft.record_id)}`}>{draft.record_id}</Link> by
          {' '}{v.requested_handle || `admin ${v.requested_by ?? '?'}`}. No model drafted it and no blind verifier ran, so it is legal tier; the engine gates below ran on it.
        </div>
        {v.reason && <p><span className="in-muted">Why:</span> {v.reason}</p>}
      </div>
    );
  }

  if (!v.rulespec) {
    return (
      <div className="in-card">
        <h3>Drafter vs verifier</h3>
        <div className="in-banner warn">No verifier RuleSpec yet ({draft.verdict || 'verify_pending'}). Verification is never skipped: this draft cannot be decided until it has one.</div>
        <Json value={draft.rulespec} open label="Drafter RuleSpec" />
      </div>
    );
  }

  return (
    <div className="in-card">
      <div className="in-row">
        <h3 style={{ margin: 0 }}>Drafter (Claude) vs verifier ({v.provider || 'openai'}{v.model ? ` · ${v.model}` : ''}, blind)</h3>
        <span className="in-spacer" />
        <VerdictBadge verdict={v.verdict || draft.verdict} />
        {redCount > 0 && <span className="in-badge bad">{redCount} field(s) differ</span>}
      </div>
      <div className="in-table-wrap" style={{ marginTop: 10 }}>
        <table className="in-table">
          <thead><tr><th>Field</th><th>Drafter</th><th>Verifier</th><th>Quotes</th></tr></thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.p} className={r.red ? 'in-diff-red' : ''}>
                <td className="in-mono">{r.p}</td>
                <td className={r.red ? 'in-v' : ''}>{show(r.a)}</td>
                <td className={r.red ? 'in-v' : ''}>{show(r.b)}</td>
                <td className="in-mono">
                  {[r.refA, r.refB].filter(Boolean).map((id) => (
                    <button key={id} type="button" className="in-chip" onClick={() => findEv(id) && onView(findEv(id))} title={findEv(id) ? findEv(id).quote : 'not found'}>{id}</button>
                  ))}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {diff.length > 0 && (
        <>
          <h4>Where they disagree, with both quotes</h4>
          {diff.map((d, i) => (
            <div key={`${d.field}-${i}`} className="in-disagree">
              <div className="in-row">
                <span className="in-mono"><b>{d.field}</b></span>
                <span className="in-badge bad">drafter {show(d.draft)}</span>
                <span className="in-badge bad">verifier {show(d.verifier)}</span>
              </div>
              {(d.quotes || []).length ? (
                <div className="in-grid2" style={{ marginTop: 6 }}>
                  <div>{(d.quotes || []).filter((q) => dEv.has(q)).map((q) => <EvidenceQuote key={q} ev={dEv.get(q)} side="drafter" onView={onView} />)}</div>
                  <div>{(d.quotes || []).filter((q) => vEv.has(q)).map((q) => <EvidenceQuote key={q} ev={vEv.get(q)} side="verifier" onView={onView} />)}</div>
                </div>
              ) : <p className="in-muted">No quote cited for this field by either side.</p>}
            </div>
          ))}
          {unmatched.length > 0 && <p className="in-muted">Structural differences: {unmatched.map((d) => d.field).join(', ')}</p>}
        </>
      )}

      <div className="in-grid2" style={{ marginTop: 12 }}>
        <div>
          <h4>Drafter evidence</h4>
          {(draft.evidence || []).map((e) => <EvidenceQuote key={e.id} ev={e} side="drafter" onView={onView} />)}
          {!(draft.evidence || []).length && <p className="in-muted">No evidence.</p>}
        </div>
        <div>
          <h4>Verifier evidence</h4>
          {(v.evidence || []).map((e) => <EvidenceQuote key={e.id} ev={e} side="verifier" onView={onView} />)}
          {(v.unlocated_quotes || []).length > 0 && (
            <div className="in-banner bad" style={{ marginTop: 8 }}>
              The verifier quoted text the server could not find in any snapshot:
              <ul className="in-violations">{v.unlocated_quotes.map((q, i) => <li key={i}>{typeof q === 'string' ? q : JSON.stringify(q)}</li>)}</ul>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ── source viewer ───────────────────────────────────────────────────── */

/** Splits text at the quote and suspicious-span boundaries; every piece is a text node. */
function markedSegments(text, quote, susp) {
  const cuts = new Set([0, text.length]);
  const ranges = [];
  if (quote) ranges.push({ ...quote, kind: 'quote' });
  for (const s of susp) ranges.push({ start: s.start, end: s.end, kind: 'susp' });
  for (const r of ranges) {
    cuts.add(Math.max(0, Math.min(text.length, r.start)));
    cuts.add(Math.max(0, Math.min(text.length, r.end)));
  }
  const pts = [...cuts].sort((a, b) => a - b);
  const out = [];
  for (let i = 0; i < pts.length - 1; i++) {
    const a = pts[i];
    const b = pts[i + 1];
    if (a === b) continue;
    const inQuote = !!quote && a >= quote.start && b <= quote.end;
    const inSusp = susp.some((s) => a >= s.start && b <= s.end);
    out.push({ a, b, inQuote, inSusp, first: inQuote && a === quote.start });
  }
  return out;
}

function locateQuote(text, ev) {
  if (!text || !ev) return null;
  const s = Number(ev.start);
  const e = Number(ev.end);
  if (Number.isInteger(s) && Number.isInteger(e) && s >= 0 && e > s && e <= text.length) return { start: s, end: e };
  const q = String(ev.quote || '').trim();
  const i = q.length >= 8 ? text.indexOf(q) : -1;
  return i >= 0 ? { start: i, end: i + q.length } : null;
}

function SourceViewer({ ev, snapshots, evidenceStore }) {
  const snap = ev ? (snapshots || []).find((s) => s.sha256_raw === ev.snapshot_sha256) : null;
  const ctype = String((snap && snap.content_type) || '').toLowerCase();
  const isPdf = ctype.includes('pdf');
  const isHtml = ctype.includes('html');
  const [mode, setMode] = useState('text');
  const [texts, setTexts] = useState({});
  const [error, setError] = useState(null);
  const [link, setLink] = useState(null);
  const boxRef = useRef(null);

  useEffect(() => { setMode('text'); setLink(null); setError(null); }, [ev]);
  useEffect(() => {
    if (!snap || mode !== 'text' || texts[snap.id]) return;
    kbfAPI.snapshot(snap.id)
      .then((r) => setTexts((t) => ({ ...t, [snap.id]: r.data.snapshot })))
      .catch((e) => setError(errMsg(e)));
  }, [snap, mode, texts]);

  const full = snap ? texts[snap.id] : null;
  const text = full ? String(full.text || '') : '';
  const quote = useMemo(() => locateQuote(text, ev), [text, ev]);
  const susp = useMemo(() => (Array.isArray(full && full.suspicious_spans) ? full.suspicious_spans : [])
    .filter((s) => Number.isInteger(s.start) && Number.isInteger(s.end) && s.end > s.start), [full]);
  const overlapsSusp = !!quote && susp.some((s) => s.start < quote.end && quote.start < s.end);
  const segments = useMemo(() => (text ? markedSegments(text, quote, susp) : []), [text, quote, susp]);

  useEffect(() => {
    const box = boxRef.current;
    const m = box && box.querySelector('mark[data-first="1"]');
    if (box && m) box.scrollTop = Math.max(0, m.offsetTop - box.offsetTop - 80);
  }, [segments]);

  const openPdf = async () => {
    setError(null);
    try {
      const r = await kbfAPI.snapshotLink(snap.id);
      setLink(r.data);
    } catch (e) {
      setError(errMsg(e));
    }
  };

  if (!ev) return <div className="in-card"><h3>Source</h3><p className="in-muted">Pick a quote (“View in source”) to see it in its stored snapshot.</p></div>;
  return (
    <div className="in-card">
      <div className="in-row">
        <h3 style={{ margin: 0 }}>Source · {ev.id}</h3>
        <span className="in-spacer" />
        <div className="in-chips" role="tablist">
          <button type="button" className={`in-chip ${mode === 'text' ? 'on' : ''}`} onClick={() => setMode('text')}>Stored text</button>
          {isHtml && evidenceStore && <button type="button" className={`in-chip ${mode === 'rendered' ? 'on' : ''}`} onClick={() => setMode('rendered')}>Rendered page (sandboxed)</button>}
          {evidenceStore && !isHtml && <button type="button" className={`in-chip ${mode === 'pdf' ? 'on' : ''}`} onClick={() => setMode('pdf')}>{isPdf ? 'PDF page' : 'Original file'}</button>}
        </div>
      </div>
      {!snap ? (
        <div className="in-banner warn" style={{ marginTop: 10 }}>The snapshot {String(ev.snapshot_sha256 || '').slice(0, 12)}… is not in kbf_snapshots.</div>
      ) : (
        <>
          <p className="in-muted" style={{ margin: '8px 0' }}>
            {snap.source_id || 'no source'} · {snap.origin}{snap.uploaded_by ? ` by admin ${snap.uploaded_by}` : ''} · fetched {fmtDate(snap.fetched_at)}
            {snap.http_status ? ` · HTTP ${snap.http_status}` : ''} · {snap.content_type || '?'} · {snap.lang || '?'}{snap.needs_ocr ? ' · needs OCR' : ''}
            {snap.changed ? ` · changed since the previous snapshot${snap.diff_summary ? `: ${snap.diff_summary}` : ''}` : ''}
            <br />
            {safeHref(snap.final_url || snap.url)
              ? <a href={safeHref(snap.final_url || snap.url)} target="_blank" rel="noopener noreferrer nofollow">{snap.final_url || snap.url}</a>
              : <span className="in-mono">{snap.final_url || snap.url}</span>}
          </p>
          {error && <div className="in-banner bad">{error}</div>}
          {mode === 'text' && (
            !full ? <div className="in-empty">Loading the stored text…</div> : (
              <>
                {!quote && <div className="in-banner warn">The quote was not found at its offsets in the stored text.</div>}
                {overlapsSusp && <div className="in-banner bad">The quote overlaps an instruction-like span on the page (possible prompt injection).</div>}
                {susp.length > 0 && <p className="in-muted">{susp.length} instruction-like span(s) on this page are marked orange.</p>}
                <div className="in-text" ref={boxRef}>
                  {segments.map((s) => (s.inQuote || s.inSusp
                    ? <mark key={s.a} className={s.inQuote ? '' : 'susp'} data-first={s.first ? '1' : undefined}>{text.slice(s.a, s.b)}</mark>
                    : <React.Fragment key={s.a}>{text.slice(s.a, s.b)}</React.Fragment>))}
                </div>
              </>
            )
          )}
          {mode === 'rendered' && (
            <>
              <p className="in-muted">The stored page is served as its own document (CSP sandbox, no scripts, nothing loads) and framed with sandbox="". The highlight is best-effort.</p>
              <iframe
                title={`snapshot ${snap.id}`}
                className="in-viewer-frame"
                sandbox=""
                referrerPolicy="no-referrer"
                src={kbfAPI.viewUrl(snap.id, ev.quote)}
              />
            </>
          )}
          {mode === 'pdf' && (
            <div style={{ marginTop: 8 }}>
              {!link ? <button type="button" className="in-btn" onClick={openPdf}>Get a 5-minute link</button> : (
                <a className="in-btn primary" href={`${link.url}${isPdf && ev.page != null ? `#page=${ev.page}` : ''}`} target="_blank" rel="noopener noreferrer">
                  Open {isPdf ? `the PDF${ev.page != null ? ` at page ${ev.page}` : ''}` : 'the file'}
                </a>
              )}
              <p className="in-muted">The file opens from the evidence store in a new tab, never inside the admin.</p>
            </div>
          )}
        </>
      )}
    </div>
  );
}

/* ── checks ──────────────────────────────────────────────────────────── */

function GateBadge({ value }) {
  const v = String(value);
  const cls = v === 'ok' ? 'ok' : /skip|n\/a/.test(v) ? '' : 'bad';
  return <span className={`in-badge ${cls}`}>{v}</span>;
}

function Checks({ draft }) {
  const val = draft.validation || null;
  const qc = draft.quotecheck || null;
  const cases = (draft.verifier && draft.verifier.cases) || [];
  const claims = draft.claims || [];
  const intended = new Set(draft.intended_deltas || []);
  const bound = new Set((qc && qc.bound) || []);
  const claimErrors = (path) => ((qc && qc.errors) || []).filter((e) => e.claim === path);
  return (
    <div className="in-card">
      <h3>Checks</h3>
      <div className="in-grid2">
        <div>
          <h4>Validation {val && <span className={`in-badge ${val.ok ? 'ok' : 'bad'}`}>{val.ok ? 'passed' : 'failed'}</span>}</h4>
          {!val ? <p className="in-muted">Not validated yet.</p> : (
            <>
              <div className="in-chips">
                {Object.entries(val.gates || {}).map(([g, r]) => <span key={g} className="in-row"><span className="in-mono">{g}</span><GateBadge value={r} /></span>)}
              </div>
              {(val.errors || []).length > 0 && <ul className="in-violations">{val.errors.map((e, i) => <li key={i}>{typeof e === 'string' ? e : JSON.stringify(e)}</li>)}</ul>}
              <h4>Fixture deltas</h4>
              {!(val.deltas || []).length ? <p className="in-muted">None.</p> : (
                <ul className="in-plain">
                  {val.deltas.map((d) => <li key={d} className={intended.has(d) ? '' : 'in-red'}>{d}{intended.has(d) ? ' (declared)' : ' (undeclared)'}</li>)}
                </ul>
              )}
              {val.kbf_layers && <p className="in-muted">{val.kbf_layers}</p>}
            </>
          )}
          {(draft.needs_fields || []).length > 0 && (
            <div className="in-banner warn" style={{ marginTop: 8 }}>Needs fields the engine does not derive yet: {draft.needs_fields.map(show).join(', ')}</div>
          )}
        </div>
        <div>
          <h4>Quote and number-role check {qc && <span className={`in-badge ${qc.ok ? 'ok' : 'bad'}`}>{qc.ok ? 'passed' : 'failed'}</span>}</h4>
          {!qc ? <p className="in-muted">Not checked yet.</p> : (
            <>
              {(qc.errors || []).length > 0 && <ul className="in-violations">{qc.errors.map((e, i) => <li key={i}><b>{e.code}</b>: {e.msg}</li>)}</ul>}
              {(qc.warnings || []).length > 0 && <ul className="in-warnings">{qc.warnings.map((w, i) => <li key={i}>{typeof w === 'string' ? w : w.msg || JSON.stringify(w)}</li>)}</ul>}
              {(qc.unbound_numbers || []).length > 0 && <p className="in-red">Numbers in the record without a bound claim: {qc.unbound_numbers.map(show).join(', ')}</p>}
              {qc.table_extraction && <div className="in-banner warn">A number was extracted from a table: legal tier.</div>}
            </>
          )}
        </div>
      </div>

      <h4>Claims (every number the record states, with its role)</h4>
      {!claims.length ? <p className="in-muted">No numeric claims.</p> : (
        <div className="in-table-wrap">
          <table className="in-table">
            <thead><tr><th>Path</th><th>Value</th><th>Unit</th><th>Comparator</th><th>Window</th><th>Evidence</th><th>Check</th></tr></thead>
            <tbody>
              {claims.map((c, i) => {
                const errs = claimErrors(c.path);
                return (
                  <tr key={`${c.path}-${i}`} className={errs.length ? 'in-diff-red' : ''}>
                    <td className="in-mono">{c.path}</td><td>{show(c.value)}</td><td>{show(c.unit)}</td><td>{show(c.comparator)}</td>
                    <td>{show(c.window)}</td><td className="in-mono">{(c.evidence || []).join(', ')}</td>
                    <td>{errs.length ? errs.map((e) => <div key={e.code} className="in-red">{e.code}</div>) : bound.has(c.path) ? <span className="in-badge ok">bound</span> : <span className="in-muted">—</span>}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <h4>Boundary cases (both RuleSpecs run on the same profiles)</h4>
      {!cases.length ? <p className="in-muted">None.</p> : (
        <div className="in-table-wrap">
          <table className="in-table">
            <thead><tr><th>Case</th><th>Profile</th><th>Expected</th><th>Actual</th><th /></tr></thead>
            <tbody>
              {cases.map((c, i) => (
                <tr key={`${c.name}-${i}`} className={c.agrees ? '' : 'in-diff-red'}>
                  <td>{c.name}</td><td className="in-mono">{show(c.profile)}</td><td>{c.expected}</td><td>{c.actual}</td>
                  <td><span className={`in-badge ${c.agrees ? 'ok' : 'bad'}`}>{c.agrees ? 'agrees' : 'disagrees'}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {(draft.truths || []).length > 0 && (
        <>
          <h4>Truth fixtures added with this draft</h4>
          <ul className="in-plain">
            {draft.truths.map((t, i) => (
              <li key={i}>{t.name}{t.must_fire ? ` · must fire ${t.must_fire.join(', ')}` : ''}{t.must_not_fire ? ` · must not fire ${t.must_not_fire.join(', ')}` : ''}</li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
}

/* ── the change itself ───────────────────────────────────────────────── */

function ChangePanel({ draft, recordDiff }) {
  const before = useMemo(() => flatten(draft.prev_record || {}), [draft.prev_record]);
  const after = useMemo(() => flatten(draft.record || {}), [draft.record]);
  const block = (draft.target === 'sidecar' ? draft.sidecar && draft.sidecar.review : draft.record && draft.record.review) || null;
  return (
    <div className="in-card">
      <h3>The change · {draft.action} → {draft.target}</h3>
      {block && block.signed_off && (
        <p><span className="in-badge ok">signed off {block.ref}</span> <span className="in-muted">by {block.by} on {block.on} · {block.tier}</span></p>
      )}
      {draft.target === 'sidecar' ? (
        <>
          <p className="in-muted">A quote sidecar: the record's own fields do not change{draft.sidecar && draft.sidecar.upgrade ? ', but it proposes a confidence upgrade (legal tier)' : ''}.</p>
          {draft.sidecar && draft.sidecar.source && (
            <table className="in-table"><tbody>
              {Object.entries(flatten(draft.sidecar.source)).map(([k, v]) => <tr key={k}><td className="in-mono">source.{k}</td><td>{show(v)}</td></tr>)}
              {draft.sidecar.upgrade && Object.entries(flatten(draft.sidecar.upgrade)).map(([k, v]) => (
                <tr key={`u${k}`} className="in-diff-red"><td className="in-mono">upgrade.{k}</td><td className="in-v">{show(v)}</td></tr>
              ))}
            </tbody></table>
          )}
          <Json value={draft.sidecar} label="Full sidecar" />
          <Json value={draft.prev_record} label="The record it quotes" />
        </>
      ) : draft.action === 'update' ? (
        !recordDiff.length ? <p className="in-muted">No field changes.</p> : (
          <div className="in-table-wrap">
            <table className="in-table">
              <thead><tr><th>Field</th><th>Before</th><th>After</th></tr></thead>
              <tbody>
                {recordDiff.map((p) => (
                  <tr key={p}><td className="in-mono">{p}</td><td className="in-muted">{show(before[p])}</td><td><b>{show(after[p])}</b></td></tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      ) : draft.action === 'signoff' ? (
        <>
          <p className="in-muted">Approve as is: the rule does not change{recordDiff.length ? ` (but ${recordDiff.join(', ')} differ)` : ''}. Approving writes its review block.</p>
          <Json value={draft.record} label="The rule" />
        </>
      ) : draft.action === 'retire' ? (
        <>
          <div className="in-banner warn">Retires {draft.record_id}.</div>
          <Json value={draft.prev_record} open label="The record being retired" />
        </>
      ) : (
        <Json value={draft.record} open label="New record" />
      )}
      {draft.action === 'update' && <Json value={draft.record} label="Full record after the change" />}
    </div>
  );
}

/* ── actions (only what apbe allows this reviewer) ───────────────────── */

function Actions({ data, onDone }) {
  const { draft, viewer, open_token: openToken, fast_threshold_s: fastS } = data;
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const [edits, setEdits] = useState('');
  const [openedAt] = useState(() => Date.now());
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  const elapsed = Math.floor((now - openedAt) / 1000);
  const fast = elapsed < (fastS || 45);
  const second = viewer.second_check;
  const disagreed = draft.verdict === 'disagreed';
  const upgrade = !!(draft.sidecar && draft.sidecar.upgrade);

  const send = async (fn, okText) => {
    setBusy(true);
    setMsg(null);
    try {
      const r = await fn();
      onDone(okText(r.data));
    } catch (e) {
      setMsg({ ok: false, text: errMsg(e) });
    } finally {
      setBusy(false);
    }
  };

  const decide = (decision) => {
    if (decision === 'approve' && disagreed && !notes.trim()) {
      setMsg({ ok: false, text: 'The verifier disagreed: write why you approve anyway.' });
      return;
    }
    if (decision === 'request_changes' && !notes.trim()) {
      setMsg({ ok: false, text: 'Say what should change.' });
      return;
    }
    if (decision === 'approve' && !window.confirm(`Approve draft ${draft.id} as ${viewer.decide_as}? This signs ${draft.record_id} (a sign-off id is recorded).`)) return;
    if (decision === 'reject' && !window.confirm(`Reject draft ${draft.id}? This is final for this revision.`)) return;
    send(() => kbfAPI.decide(draft.id, { decision, notes, open_token: openToken }), (d) => (decision === 'approve'
      ? `Approved · sign-off ${d.signoff_id}${d.second_check_task ? ` · second check opened (task ${d.second_check_task})` : ''}`
      : decision === 'reject' ? 'Rejected.' : 'Sent back for changes; the job is requeued.'));
  };

  const secondCheck = (decision) => {
    if (decision === 'reject' && !notes.trim()) {
      setMsg({ ok: false, text: 'Say why the second check fails.' });
      return;
    }
    send(() => kbfAPI.decide(draft.id, { decision, second_check: true, notes, open_token: openToken }),
      (d) => (decision === 'approve' ? 'Second check confirmed.' : `Second check failed${d.follow_up_task ? ` · task ${d.follow_up_task} raised to revert the published files` : ''}.`));
  };

  const saveEdits = () => {
    let parsed;
    try {
      parsed = JSON.parse(edits);
    } catch {
      setMsg({ ok: false, text: 'Edits must be a JSON object of record fields, e.g. {"title": "…"}' });
      return;
    }
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
      setMsg({ ok: false, text: 'Edits must be a JSON object of record fields.' });
      return;
    }
    send(() => kbfAPI.edit(draft.id, { edits: parsed, notes, open_token: openToken }), () => 'Edited: the draft goes back to validation (revalidate).');
  };

  return (
    <div className="in-card in-actions">
      <div className="in-row">
        <h3 style={{ margin: 0 }}>Decision</h3>
        <span className="in-spacer" />
        <RoleChips viewer={viewer} />
      </div>
      {msg && <div className={`in-banner ${msg.ok ? 'info' : 'bad'}`} style={{ marginTop: 10 }}>{msg.text}</div>}

      {second && (
        <div className="in-banner info" style={{ marginTop: 10 }}>
          Second check open (task {second.task_id}), first approved by admin {second.first_admin_id ?? '?'}.
          {second.can ? ' You may confirm or fail it.' : ` ${second.reason || 'You cannot do this second check.'}`}
        </div>
      )}

      {(viewer.can_decide || (second && second.can)) && (
        <label className="in-field" style={{ marginTop: 10 }}>
          Notes{disagreed && viewer.can_decide ? ' (required to approve: the verifier disagreed)' : ''}
          <textarea className="in-textarea" value={notes} onChange={(e) => setNotes(e.target.value)} maxLength={4000} />
        </label>
      )}

      {viewer.can_decide ? (
        <>
          {upgrade && (
            <p className={viewer.upgrade_applies ? 'in-muted' : 'in-red'}>
              {viewer.upgrade_applies
                ? 'Your approval keeps the proposed confidence upgrade.'
                : 'Your approval drops the proposed upgrade: the record stays moderate (an upgrade needs a legal approver, and a bilingual reviewer for a non-English source).'}
            </p>
          )}
          {fast && <p className="in-muted">Approving within {fastS || 45} s of opening ({elapsed} s so far) marks it fast and sends it to a second check.</p>}
          <div className="in-row">
            <button type="button" className="in-btn primary" disabled={busy} onClick={() => decide('approve')}>
              Approve as {viewer.decide_as}
            </button>
            {draft.verdict !== 'manual' && <button type="button" className="in-btn" disabled={busy} onClick={() => decide('request_changes')}>Request changes</button>}
            <button type="button" className="in-btn danger" disabled={busy} onClick={() => decide('reject')}>Reject</button>
          </div>
          {draft.action !== 'signoff' && <details style={{ marginTop: 12 }}>
            <summary className="in-muted">Edit record fields (sends the draft back to validation)</summary>
            <textarea className="in-textarea in-mono" value={edits} onChange={(e) => setEdits(e.target.value)} placeholder='{"title": "…", "plain_language": "…"}' />
            <button type="button" className="in-btn small" disabled={busy || !edits.trim()} onClick={saveEdits}>Save edits</button>
          </details>}
        </>
      ) : (
        !second && <p className="in-muted" style={{ marginTop: 10 }}>{viewer.why_not || 'You cannot decide this draft.'}</p>
      )}

      {second && second.can && (
        <div className="in-row" style={{ marginTop: 10 }}>
          <button type="button" className="in-btn primary" disabled={busy} onClick={() => secondCheck('approve')}>Confirm (second check)</button>
          <button type="button" className="in-btn danger" disabled={busy} onClick={() => secondCheck('reject')}>Fail the second check</button>
        </div>
      )}
    </div>
  );
}

function History({ data }) {
  const { reviews = [], tasks = [], revisions = [], publications = [], draft } = data;
  return (
    <div className="in-card">
      <h3>Review history</h3>
      {!reviews.length ? <p className="in-muted">No decisions yet.</p> : (
        <div className="in-table-wrap">
          <table className="in-table">
            <thead><tr><th>When</th><th>Who</th><th>Role</th><th>Decision</th><th>Sign-off</th><th>Flags</th><th>Notes</th></tr></thead>
            <tbody>
              {reviews.map((r) => (
                <tr key={r.id}>
                  <td className="in-muted">{fmtDate(r.created_at)}</td>
                  <td>{r.admin_name || r.admin_email || `admin ${r.admin_id}`}</td>
                  <td>{r.role}</td>
                  <td><span className={`in-badge ${r.decision === 'approve' ? 'ok' : r.decision === 'reject' ? 'bad' : ''}`}>{r.decision}</span></td>
                  <td className="in-mono">{r.signoff_id || '—'}</td>
                  <td className="in-muted">{[Number(r.second_check) ? 'second check' : null, Number(r.fast) ? 'fast' : null, r.reason].filter(Boolean).join(' · ') || '—'}</td>
                  <td className="in-muted" style={{ maxWidth: 360 }}>{r.notes || ''}{r.edits_json ? ` edits: ${JSON.stringify(r.edits_json)}` : ''}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {tasks.length > 0 && (
        <>
          <h4>Tasks</h4>
          <ul className="in-plain">{tasks.map((t) => <li key={t.id}>#{t.id} {t.kind} · {t.status}{t.detail && t.detail.outcome ? ` · ${t.detail.outcome}` : ''}{t.detail && t.detail.to ? ` → ${t.detail.to}` : ''}</li>)}</ul>
        </>
      )}
      {revisions.length > 1 && (
        <>
          <h4>Revisions</h4>
          <div className="in-chips">
            {revisions.map((r) => (r.id === draft.id
              ? <span key={r.id} className="in-chip on">rev {r.revision} · {r.status}</span>
              : <Link key={r.id} className="in-chip" to={`/intelligence/kb/drafts/${r.id}`}>rev {r.revision} · {r.status}</Link>))}
          </div>
        </>
      )}
      {publications.length > 0 && (
        <>
          <h4>Publications</h4>
          <ul className="in-plain">
            {publications.map((p) => (
              <li key={p.id}>{p.status} · {p.branch}{p.pr_number ? ` · PR #${p.pr_number}` : ''}{p.on_staging ? ' · on staging' : ''}
                {safeHref(p.pr_url) && <> · <a href={safeHref(p.pr_url)} target="_blank" rel="noopener noreferrer">open</a></>}</li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
}

export default function IntelligenceKbDraft() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [evidenceStore, setEvidenceStore] = useState(false);
  const [selected, setSelected] = useState(null);
  const [flash, setFlash] = useState(null);
  const viewerRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const r = await kbfAPI.draft(id);
      setData(r.data);
      setError(null);
      setSelected((s) => s || ((r.data.draft.evidence || [])[0]) || null);
    } catch (e) {
      setError(errMsg(e));
    }
  }, [id]);
  useEffect(() => {
    setData(null);
    setSelected(null);
    setFlash(null);
    load();
    kbfAPI.summary().then((r) => setEvidenceStore(!!(r.data.config && r.data.config.evidence_store))).catch(() => setEvidenceStore(false));
  }, [load]);

  const view = (ev) => {
    setSelected(ev);
    if (viewerRef.current) viewerRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const d = data && data.draft;
  const eff = data && data.effective_tier;
  return (
    <div className="dashboard-layout">
      <Sidebar />
      <main className="dashboard-main">
        <div className="dashboard-header">
          <div className="header-left">
            <p className="in-muted" style={{ margin: 0 }}><Link to="/intelligence/kb">← KB REVIEW</Link>
              {d && <> · <Link to={`/intelligence/kb/rules/${encodeURIComponent(d.record_id)}`}>{d.record_id} in KB RULES</Link></>}</p>
            <h1 className="page-title">DRAFT {id}{d ? ` · ${d.record_id}` : ''}</h1>
            {d && (
              <div className="in-row">
                <TierBadge tier={eff.tier} />
                <VerdictBadge verdict={d.verdict} />
                <span className="in-badge">{d.status}</span>
                <span className="in-muted">{d.action} → {d.target} · rev {d.revision} · job {d.job_id} · {fmtDate(d.created_at)}</span>
              </div>
            )}
          </div>
        </div>
        {error && <div className="in-banner bad">{error}</div>}
        {!data ? (!error && <div className="in-empty">Loading the draft…</div>) : (
          <>
            {eff.reasons && eff.reasons.length > 0 && (
              <div className="in-banner info">Legal tier because: {eff.reasons.join('; ')}.{eff.mismatch ? ` The factory stored “${eff.stored}”; apbe enforces the stricter tier.` : ''}</div>
            )}
            {data.job && <p className="in-muted">Question: {data.job.question}</p>}
            <RuleSpecCompare draft={d} onView={view} />
            <ChangePanel draft={d} recordDiff={data.record_diff || []} />
            <div ref={viewerRef}><SourceViewer ev={selected} snapshots={data.snapshots} evidenceStore={evidenceStore} /></div>
            <Checks draft={d} />
            <History data={data} />
            {flash && <div className="in-banner info">{flash}</div>}
            {/* a fresh open token on every load: the fast timer restarts with it */}
            <Actions key={data.open_token} data={data} onDone={(text) => { setFlash(text); load(); }} />
          </>
        )}
      </main>
    </div>
  );
}
